import { DatePicker } from 'restaurantAdmin/components/datePicker/DatePicker';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import {
  isReservation,
  ServiceOccupant,
  ServiceReservation,
  ServiceWalkIn,
} from 'restaurantAdmin/reservations/service/apiHelpers';
import { useOccupantsQuery } from 'restaurantAdmin/reservations/service/hooks/useOccupantsQuery';
import { useServiceSearchParams } from 'restaurantAdmin/reservations/service/hooks/useServiceSearchParams';
import { SERVICE_STATUS_COLORS } from 'restaurantAdmin/reservations/service/serviceStatus/ServiceStatus';
import { ServiceStatusIcon } from 'restaurantAdmin/reservations/service/serviceStatus/ServiceStatusIcon';
import { Icon } from '@components/icon/Icon';
import { LoadingPage } from '@components/LoadingPage';
import { Switch } from '@components/switch/Switch';
import type { DateSelectArg } from '@fullcalendar/core';
import { EventInput } from '@fullcalendar/core';
import interactionPlugin, {
  type DateClickArg,
} from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { ResourceLabelContentArg } from '@fullcalendar/resource/index.js';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { Error } from '@shared/components/error/Error';
import { useReservationServiceContext } from 'restaurantAdmin/reservations/service/state/ReservationServiceContext';
import { HostFloorPlanData } from 'restaurantAdmin/floorPlans/apiHelpers';
import { createReferenceDate } from '@shared/utils/createReferenceDate';
import { dateToISOTime, ISOTimeAddMinutes } from '@shared/utils/time';
import { useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import { add, subHours } from 'date-fns';
import { orderBy } from 'lodash-es';
import { useState } from 'react';
import typography from '~styles/typography.scss';
import { FloorPlanTable, getFloorPlanTables } from './apiHelpers';
import { ReservationTimelineBookModal } from './ReservationTimelineBookModal';
import styles from './ReservationTimelinePage.scss';

const DEFAULT_OCCUPANT_DURATION_IN_MINUTES = 60;

// The timeline view only cares about occupants that will or have impacted
// service. So, for example, an occupant that cancelled their reservation should
// not be shown as that would not impact service in the vast majority of cases.
const isOccupantRelevantToService = (occupant: ServiceOccupant) => {
  return (
    occupant.status === 'active' ||
    occupant.status === 'finished' ||
    occupant.status === 'noShow' ||
    occupant.status === 'autoResolved'
  );
};

const occupantToFullCalendarEvents = (occupant: ServiceOccupant) => {
  if (isReservation(occupant)) {
    return reservationToFullCalendarEvents(occupant);
  } else {
    return walkInToFullCalendarEvents(occupant);
  }
};

const reservationToFullCalendarEvents = (
  reservation: ServiceReservation,
): EventInput[] => {
  const floorPlanTablesOnReservation = reservation.tableAssignments?.flatMap(
    (tableAssignment) => tableAssignment.floorPlanTables,
  );
  // If a reservation is seated we should associated the reservation with the
  // table(s) it was seated at, otherwise use the assigned table(s).
  const resourceIds = (
    reservation.seatings.length > 0
      ? reservation.seatings.map((s) => s.floorPlanTableId)
      : floorPlanTablesOnReservation.map((t) => t?.id)
  ).filter((id) => id !== undefined && id !== null);

  const duration =
    reservation.duration ||
    reservation.turnTime ||
    DEFAULT_OCCUPANT_DURATION_IN_MINUTES;
  return resourceIds.map((resourceId, index) => ({
    resourceId,
    id: `${reservation.id}-${index}`,
    title: `${reservation.guest.firstName} ${reservation.guest.lastName}`,
    start: createReferenceDate(reservation.time),
    end: createReferenceDate(ISOTimeAddMinutes(reservation.time, duration)),
    extendedProps: {
      guestCount: reservation.guestCount,
      serviceStatus: reservation.serviceStatus,
      occupantId: reservation.id,
      occupantType: 'reservation',
      isPaid: reservation.transactionDetails.price > 0,
    },
  }));
};

const walkInToFullCalendarEvents = (walkIn: ServiceWalkIn): EventInput[] => {
  // A walk-in will always be seated so we should associated the walk-in with
  // the table(s) it was seated at
  const resourceIds = walkIn.seatings
    .map((s) => s.floorPlanTableId)
    .filter((id) => id !== null);

  const firstName = walkIn.guest?.firstName || walkIn.firstName;
  const lastName = walkIn.guest?.lastName || walkIn.lastName;
  const title = firstName && lastName ? `${firstName} ${lastName}` : 'Walk-In';
  const duration = DEFAULT_OCCUPANT_DURATION_IN_MINUTES;
  return resourceIds.map((resourceId, index) => ({
    resourceId,
    id: `${walkIn.id}-${index}`,
    title,
    start: createReferenceDate(walkIn.time),
    end: createReferenceDate(ISOTimeAddMinutes(walkIn.time, duration)),
    extendedProps: {
      guestCount: walkIn.guestCount,
      serviceStatus: walkIn.serviceStatus,
      occupantId: walkIn.id,
      occupantType: 'walkIn',
      isPaid: false,
    },
  }));
};

const floorPlanTableToFullCalendarResource = (
  floorPlanTable: FloorPlanTable,
) => {
  return {
    id: floorPlanTable.id,
    title: floorPlanTable.name,
    floorPlanName: floorPlanTable.floorPlanName,
    extendedProps: {
      iconName: floorPlanTable.iconName,
      floorPlanId: floorPlanTable.floorPlanId,
    },
  };
};

const filterFloorPlansBySelectedDate = (
  floorPlanTables?: FloorPlanTable[],
  floorPlansBySelectedDate?: HostFloorPlanData[],
) => {
  return floorPlanTables?.filter((floorPlan) =>
    floorPlansBySelectedDate?.some((fp) => fp.id === floorPlan.floorPlanId),
  );
};

const renderResourceContent = (resourceInfo: ResourceLabelContentArg) => {
  return (
    <>
      <span>{resourceInfo.resource.title}</span>
      <Icon
        name={resourceInfo.resource.extendedProps.iconName}
        className={styles.tableIcon}
      />
    </>
  );
};

export const ReservationTimelinePage = () => {
  const {
    selectedDate: date,
    setSelectedDate: updateDate,
    floorPlansBySelectedDate,
  } = useReservationServiceContext();
  const { openOccupantDetailsDrawer } = useServiceSearchParams();
  const { id: restaurantId } = useRestaurant();
  const {
    data: { occupants },
    isLoading: isLoadingOccupants,
  } = useOccupantsQuery();

  const [
    onlyShowFloorPlanTablesWithReservations,
    setOnlyShowFloorPlanTablesWithReservations,
  ] = useState(false);

  const [isCreateReservationModalOpen, setIsCreateReservationModalOpen] =
    useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<{
    start: Date;
    end: Date;
    resourceId: string;
    floorPlanId: string;
  } | null>(null);

  const toggleOnlyShowFloorPlanTablesWithReservations = () => {
    setOnlyShowFloorPlanTablesWithReservations((prev) => !prev);
  };

  const setSelectedDateTimeRangeFromDate = (
    info: DateClickArg | DateSelectArg,
    isSelection: boolean = false,
  ) => {
    if (!info.resource) return;
    const floorPlanId = info.resource.extendedProps?.floorPlanId as
      | string
      | undefined;
    if (!floorPlanId) return;
    const start = new Date('start' in info ? info.start : info.date);
    let end = new Date(start.getTime() + 60 * 60 * 1000);
    if (isSelection && 'end' in info) {
      end = new Date(info.end);
    }
    setSelectedDateTime({
      start,
      end,
      resourceId: info.resource.id,
      floorPlanId,
    });
    setIsCreateReservationModalOpen(true);
  };

  const handleDateClick = (info: DateClickArg) => {
    setSelectedDateTimeRangeFromDate(info);
  };

  const handleSelect = (info: DateSelectArg) => {
    setSelectedDateTimeRangeFromDate(info, true);
  };

  const closeCreateReservationModal = () => {
    setIsCreateReservationModalOpen(false);
    setSelectedDateTime(null);
  };

  const { data: floorPlanTables, isLoading: isLoadingTables } = useQuery({
    queryKey: ['floorPlanTables', restaurantId],
    queryFn: () => getFloorPlanTables(restaurantId),
  });

  if (isLoadingTables || isLoadingOccupants) {
    return <LoadingPage />;
  }

  const calendarEvents: EventInput[] = occupants
    .filter((occupant) => isOccupantRelevantToService(occupant))
    .flatMap((occupant) => occupantToFullCalendarEvents(occupant));
  const calendarResources = orderBy(
    filterFloorPlansBySelectedDate(
      floorPlanTables,
      floorPlansBySelectedDate,
    )?.map(floorPlanTableToFullCalendarResource),
    'floorPlanName',
  );
  const hasCalendarResources =
    calendarResources && calendarResources.length > 0;

  return (
    <>
      <PageHeader
        customNode={
          <DatePicker
            handleOnChangeCalendarDate={(date) => {
              updateDate(date);
            }}
            handleOnClickNextDay={() => {
              updateDate(add(date, { days: 1 }));
            }}
            handleOnClickPrevDay={() => {
              updateDate(add(date, { days: -1 }));
            }}
            selectedDate={date}
          />
        }
      ></PageHeader>
      <PageContent className={styles.container}>
        <div className={styles.resourceToggle}>
          <Switch
            checked={onlyShowFloorPlanTablesWithReservations}
            onCheckedChange={toggleOnlyShowFloorPlanTablesWithReservations}
          />
          <span>Only show tables with reservations</span>
        </div>

        {hasCalendarResources ? (
          <FullCalendar
            scrollTime={dateToISOTime(subHours(new Date(), 1))} // current time minus 1 hour for padding
            height="100%"
            stickyHeaderDates={true}
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            headerToolbar={false}
            plugins={[resourceTimelinePlugin, interactionPlugin]}
            slotMinTime="04:00:00"
            slotMaxTime="28:00:00"
            nowIndicator={true}
            slotDuration="00:15"
            slotLabelInterval="00:15"
            initialView="resourceTimeline"
            resourceAreaWidth="225px"
            resourceOrder="title"
            resourceAreaHeaderClassNames={styles.resourceHeaderLabel}
            filterResourcesWithEvents={onlyShowFloorPlanTablesWithReservations}
            resourceAreaHeaderContent={<div></div>}
            resourceLabelClassNames={cx(typography.h7, styles.tableLabel)}
            resourceGroupField="floorPlanName"
            resourceGroupLaneClassNames={styles.resourceGroupLane}
            resourceGroupLabelClassNames={styles.resourceGroupLabel}
            slotLabelClassNames={cx(typography.h7m, styles.slotLabel)}
            resources={calendarResources}
            resourceLabelContent={renderResourceContent}
            events={calendarEvents}
            selectable={true}
            select={handleSelect}
            dateClick={handleDateClick}
            eventContent={(eventInfo) => {
              const {
                guestCount,
                serviceStatus,
                occupantId,
                occupantType,
                isPaid,
              } = eventInfo.event.extendedProps;

              const serviceStatusColor =
                SERVICE_STATUS_COLORS[
                  serviceStatus as ServiceReservation['serviceStatus']
                ];
              return (
                <div
                  role="button"
                  className={cx(styles.eventContent)}
                  style={{
                    borderColor: serviceStatusColor,
                    backgroundColor: serviceStatusColor,
                  }}
                  onClick={() => {
                    openOccupantDetailsDrawer({
                      id: occupantId,
                      type: occupantType,
                    });
                  }}
                >
                  {isPaid && (
                    <div
                      className={styles.paidReservationIcon}
                      aria-hidden="true"
                      data-testid="paid-reservation-icon"
                    >
                      <Icon
                        name="dollar"
                        role="img"
                        ariaLabel="Paid reservation"
                      />
                    </div>
                  )}
                  <ServiceStatusIcon
                    serviceStatus={serviceStatus}
                    scale={0.5}
                    className={styles.eventIcon}
                  />
                  <div className={styles.eventDescription}>
                    <span className={styles.guestName}>
                      {eventInfo.event.title}
                    </span>
                    <span className={styles.guestCount}>{guestCount}</span>
                  </div>
                </div>
              );
            }}
          />
        ) : (
          <Error message="No floor plan tables found." />
        )}
      </PageContent>
      <ReservationTimelineBookModal
        isOpen={isCreateReservationModalOpen}
        closeModal={closeCreateReservationModal}
        selectedDate={date}
        selectedTime={
          selectedDateTime ? dateToISOTime(selectedDateTime.start) : undefined
        }
        selectedDuration={
          selectedDateTime
            ? Math.round(
                (selectedDateTime.end.getTime() -
                  selectedDateTime.start.getTime()) /
                  (1000 * 60),
              )
            : undefined
        }
        selectedTableId={selectedDateTime?.resourceId}
        selectedFloorPlanId={selectedDateTime?.floorPlanId}
      />
    </>
  );
};
